/**
 * `<ProgressNoteViewer>` — locked-note chrome for a Progress Note
 * `DocumentReference`. The existing `<Documents>` component handles
 * heavy CCDA rendering; this component is the "lock lifecycle + scribe
 * summary + top action group" chrome above it.
 *
 * Layout (top → bottom, matches mockup `ChartPage.tsx:3919-4093`):
 *   1. Locked banner            — `docStatus === 'final'` only
 *   2. Action group             — Review Followup / Export to PDF /
 *                                 Edit / Lock Note (or Locked · TEFCA)
 *   3. Stats Strip              — any structured Progress Note (LD-21)
 *   4. Table of Contents        — any structured Progress Note (LD-21)
 *   5. Billing Summary          — when an AI suggestion is supplied (read-only)
 *   6. Locked sections          — `docStatus === 'final'` only, hydrates QR/Claim
 *
 * States:
 *  1. `docStatus === 'preliminary'`
 *     → Action group renders Edit + "Lock Note" filled blue. Clicking the
 *       lock button opens a Mantine confirm modal (one-line warning + cyan
 *       TEFCA info box). Confirm fires `useLockAndTransmit`, which POSTs
 *       `/visits/:encounterId/lock` and on success flips the doc to
 *       `final`. While the mutation is in flight, the Confirm + Cancel
 *       buttons are disabled and Confirm shows a loader.
 *
 *  2. `docStatus === 'final'`
 *     → Renders the full-width navy "Note Locked" banner with a green
 *       "TEFCA Transmission Queued" badge on the right (mockup
 *       `ChartPage.tsx:3919-3929`), and a "Locked · TEFCA" pill replaces
 *       the Lock button in the action group (mockup line 3980-3985).
 *
 * Action handlers (Review Followup / Export to PDF / Edit) are owned by
 * the caller — Documents.tsx passes click handlers + state down so this
 * component stays presentation-only. That keeps a single source of truth
 * for state (modal open, edit panel open, export in flight) in the page.
 *
 * Phase 6 polish:
 *   - 6.1 single-button confirm via `modals.openConfirmModal`
 *   - 6.2 "Lock and Transmit" → "Lock Note"
 *   - 6.3 badge "Locked & transmitted to HG" → "Locked & TEFCA-queued"
 *   - 6.5 spinner + disabled state during the lock mutation
 *   - 6.6 mount `<NoteStatsStrip>` (when scribe-backed) above the lock chrome
 *   - 6.7 billing summary rendered via `<BillingSummary>` (in PlanSection) for the locked view
 *   - 6.8 (mockup parity) replace the small green badge with a full-width
 *         navy "Note Locked" banner + green "TEFCA Transmission Queued"
 *         chip on the right, including the lock date sourced from
 *         `documentReference.meta.lastUpdated` (the timestamp the lock
 *         endpoint flipped the doc to `final`).
 *   - LD-21 (mockup parity) hoist Review Followup / Export to PDF / Edit
 *         action buttons INTO the viewer so the locked-note chrome is the
 *         single source of truth for top-right actions; Documents.tsx
 *         continues to own the underlying state.
 *
 * Phase 6.4: the hook (`useLockAndTransmit`) hardcodes `attested: true`
 * internally — the UI no longer surfaces a checkbox; the Lock click IS
 * the signature per HL7 (`Provenance.activity = 'AU'`).
 *
 * Encounter resolution:
 *   The lock endpoint requires `:encounterId`. The Progress Note
 *   `DocumentReference` itself does NOT carry that reference; the sibling
 *   Provenance.entity holds an `Encounter/...` ref. The caller resolves
 *   this once (Documents.tsx) and passes it down so this component stays
 *   stateless on data-fetch.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useMedplum, useResource } from '@medplum/react'
import {
  IconCheck,
  IconDownload,
  IconEdit,
  IconExternalLink,
  IconEye,
  IconInfoCircle,
  IconLock,
  IconWriting,
} from '@tabler/icons-react'
import type {
  DocumentReference,
  Encounter,
  Provenance,
  QuestionnaireResponse,
} from '@medplum/fhirtypes'
import { LockedProgressNoteSections } from './ProgressNoteViewer/LockedProgressNoteSections'
import { NoteMetadataGrid } from './ProgressNoteViewer/NoteMetadataGrid'
import { NoteStatsStrip } from './ProgressNoteViewer/NoteStatsStrip'
import {
  hasScribeDraftPayload,
  isCareCoordinatorNote,
  resolveNoteRenderMode,
  type NoteRenderMode,
} from './ProgressNoteViewer/scribeDetection'
import { SessionTranscriptAccordion } from './ProgressNoteViewer/SessionTranscriptAccordion'
import { TableOfContents } from './ProgressNoteViewer/TableOfContents'
import { TocPresenceProvider } from './ProgressNoteViewer/TocPresenceProvider'
import { AddendaSection } from './ProgressNoteViewer/AddendaSection'
import { AddendumEditorModal } from './ProgressNoteViewer/AddendumEditorModal'
import { useNoteAddenda } from '@/pages/Patient/hooks/useNoteAddenda'
import { useLockAndTransmit } from '@/pages/Patient/hooks/useLockAndTransmit'
import { useIsManager } from '@/hooks/useIsManager'
import { qrDraftCounts, scribeDraftCounts } from './ProgressNoteViewer/qrDraftCounts'
import {
  readScribeDraftPayload,
  resolvableLowConfidenceKeys,
} from './ProgressNoteEdit/scribeDraftPayload'
import { PROGRESS_NOTE_CORAL } from './ProgressNoteEdit/tokens'
import { logError } from '@/utils/errorUtils'
import { formatDateTime } from '@/utils'
import type { NoteSignature } from '@/utils/provenance'

const LOCK_MODAL_ID = 'lock-progress-note'

// Dark-navy confirm button with a leading lock icon, matching the mockup
// (`ChartPage.tsx:4424-4452`). Reused across the open + loading + reset modal
// states so the styling/icon survive the in-flight `updateModal` calls.
const LOCK_CONFIRM_PROPS = {
  color: '#1e3a5f',
  leftSection: <IconLock size={16} />,
}

export type ProgressNoteViewerProps = {
  documentReference: DocumentReference
  /**
   * Pre-resolved render mode from `resolveNoteRenderMode`. Optional — when
   * omitted the viewer resolves it itself from `documentReference` +
   * `provenance` (keeps standalone callsites / tests simple). `Documents.tsx`
   * passes it explicitly so the CCDA-table suppression and the structured /
   * external chrome are driven by the SAME decision and can never diverge.
   */
  renderMode?: NoteRenderMode
  /**
   * Encounter id the lock call mutates against. If this is undefined the
   * Lock button stays disabled and surfaces a tooltip — the backend cannot
   * compute the lock without an encounter context.
   */
  encounterId?: string
  /**
   * Encounter snapshot used to drive the Session Duration card and the
   * encounter-scoped FHIR count queries inside `<NoteStatsStrip>`. Optional —
   * when omitted, the strip falls back to "—" for duration and 0 for counts.
   */
  encounter?: Encounter
  /**
   * Provenance for this Progress Note. The Stats Strip and the locked-sections
   * panel use it to resolve the linked `QuestionnaireResponse` / `Claim`. LD-21
   * removed the strict scribe-only gate — the structured chrome now renders
   * for every Progress Note (locked OR Device-attributed preliminary).
   */
  provenance?: Provenance
  /** The note's electronic signature (PD-1897) — set only once it is signed. */
  signature?: NoteSignature
  /**
   * When `true`, render a "Review Followup" outline button in the action
   * group. The caller (Documents.tsx) owns the `ReviewFollowupModal` and
   * supplies the click handler. The button is hidden when undefined.
   */
  showReviewFollowup?: boolean
  /** Click handler for the "Review Followup" button (modal opener). */
  onReviewFollowup?: () => void
  /**
   * When `true`, the patient-instructions followup has already been emailed
   * for this note (a `PatientInstructions` DocumentReference relates to it).
   * The button label stays "Patient Instructions" either way, but a sent note
   * swaps the leading icon to a check + green accent so the doctor can see at
   * a glance that instructions already went out. Sourced from `useFollowupStatus`.
   */
  followupSaved?: boolean
  /** In-flight flag for the followup-status lookup; shows a spinner on the button. */
  followupLoading?: boolean
  /**
   * When `true`, render an "Export to PDF" outline button in the action
   * group. The caller owns the PDF export (CCDA → pdfmake) and supplies
   * both the click handler and the in-flight loading flag.
   */
  showExportToPdf?: boolean
  onExportToPdf?: () => void
  isExporting?: boolean
  /**
   * When `true`, render the "Edit" button in the action group. The viewer
   * does NOT own edit state — Documents.tsx flips its own `showEdit` flag
   * and re-renders the `<ProgressNoteEdit>` panel beneath.
   */
  canEdit?: boolean
  /** Whether Documents.tsx currently has the edit panel open. */
  isEditing?: boolean
  /** Click handler for the Edit / Cancel toggle. */
  onToggleEdit?: () => void
  /**
   * Optional DOM element where the action group (Review Followup / Export to
   * PDF / Edit / Lock Note / Locked·TEFCA pill) should be portal-rendered
   * instead of inline above the Stats Strip. Used by `Documents.tsx` to hoist
   * the actions into the "Progress Note" title row per mockup
   * `ui-designs/.../ChartPage.tsx:3951-3987`. When `null` (default), the
   * actions render inline as before.
   */
  actionGroupTarget?: HTMLElement | null
  /**
   * Optional DOM element where the locked banner ("Note Locked" full-width
   * navy ribbon) should be portal-rendered. Used by `Documents.tsx` to hoist
   * the banner to the very top of the document area — above the title and
   * metadata — per mockup `ui-designs/.../ChartPage.tsx:3919-3929`. When
   * `null` (default), the banner renders inline at the top of the viewer.
   */
  lockedBannerTarget?: HTMLElement | null
  /**
   * Called once the Lock mutation resolves successfully. The viewer itself is
   * stateless on cached resources — it relies on the parent (`Documents.tsx`)
   * to re-fetch the DocumentReference + Provenance so the UI flips from the
   * preliminary "Lock Note" chrome to the locked banner / "Locked · TEFCA"
   * pill. Without this, the viewer would keep rendering the stale
   * `docStatus: 'preliminary'` state until the user manually reloads.
   */
  onLockSuccess?: () => void
  /**
   * Patient id forwarded to the BillingEditModal's `<DxCodingSelect>` for
   * the patient's-recent-active-problems autocomplete.
   */
  patientId?: string
}

export function ProgressNoteViewer({
  documentReference,
  renderMode,
  encounterId,
  encounter,
  provenance,
  signature,
  showReviewFollowup,
  onReviewFollowup,
  followupSaved,
  followupLoading,
  showExportToPdf,
  onExportToPdf,
  isExporting,
  canEdit,
  isEditing,
  onToggleEdit,
  actionGroupTarget,
  lockedBannerTarget,
  onLockSuccess,
  patientId,
}: ProgressNoteViewerProps) {
  // A draft removed from the chart (PD-2031) is no longer editable or lockable.
  const isPreliminary =
    documentReference.docStatus === 'preliminary' && documentReference.status !== 'entered-in-error'
  const isFinal = documentReference.docStatus === 'final'

  const medplum = useMedplum()
  const lock = useLockAndTransmit()
  // Managers (care coordinators) never see the transcript or lock the note.
  // Fail-open (false while loading).
  const { isManager } = useIsManager()

  // Footer attribution — resolve the encounter's owning Organization name.
  const serviceOrg = useResource(encounter?.serviceProvider)

  // Option A: Lock no longer reconstructs (or sends) the note — the backend
  // materializes from the lossless draft blob it stored at save time. The
  // draft `QuestionnaireResponse` is still fetched here for ONE reason: the
  // Stats Strip's PRELIMINARY draft counts (`qrDraftCounts`) read row counts
  // off it. The QR id lives on the sibling Provenance.target. Final notes
  // don't need it (the locked sections + live counts cover them). Fetch is
  // best-effort: a failure leaves `draftQr` undefined (counts fall back to 0).
  const [draftQr, setDraftQr] = useState<QuestionnaireResponse | undefined>(undefined)
  const questionnaireResponseId = useMemo(
    () =>
      provenance?.target
        ?.map((t) => t.reference)
        .find((r) => r?.startsWith('QuestionnaireResponse/'))
        ?.split('/')[1],
    [provenance],
  )

  useEffect(() => {
    if (!isPreliminary || !questionnaireResponseId) {
      setDraftQr(undefined)
      return
    }
    let active = true
    medplum
      .readResource('QuestionnaireResponse', questionnaireResponseId)
      .then((qr) => {
        if (active) setDraftQr(qr)
      })
      .catch((err) => {
        logError('progress-note-viewer', 'Failed to load draft QR for stats', err)
      })
    return () => {
      active = false
    }
  }, [medplum, isPreliminary, questionnaireResponseId])

  // PRELIMINARY stat-card counts: blob-first (DocRef is readable), QR fallback —
  // so the cards match the blob-fed body when the draft QR can't be read.
  const draftFallbackCounts = useMemo(() => {
    if (!isPreliminary) return undefined
    const scribeDraft = readScribeDraftPayload(documentReference)
    return scribeDraft ? scribeDraftCounts(scribeDraft.soapNote) : qrDraftCounts(draftQr)
  }, [isPreliminary, documentReference, draftQr])

  /**
   * Render mode — prefer the value `Documents.tsx` resolved (so the viewer's
   * structured chrome and the parent's CCDA-table suppression agree), falling
   * back to resolving it locally for standalone callsites / tests.
   */
  const mode = useMemo<NoteRenderMode>(
    () => renderMode ?? resolveNoteRenderMode(documentReference, provenance),
    [renderMode, documentReference, provenance],
  )

  /**
   * Structured = our Progress Note with structured data to fill the 5-section
   * layout (Stats Strip + TOC + sections). Never-blank fix: external imports
   * (`external-document`) and structured-less fallbacks (`document-fallback`)
   * are NOT structured — they show the original CCDA document below instead of
   * empty structured sections. The 308 QR-backed notes resolve to
   * `structured-readonly` (final) / `structured-editable` (preliminary) and
   * keep the exact chrome they render today.
   */
  const structured = mode === 'structured-editable' || mode === 'structured-readonly'
  const isExternal = mode === 'external-document'

  // Addenda exist on locked notes only. Everyone who can read the note reads
  // them; only non-managers can write one — enforced again by the AccessPolicy,
  // which withholds write on this document type.
  const canViewAddenda =
    isFinal && structured && !isExternal && !isCareCoordinatorNote(documentReference)
  const canAmend = canViewAddenda && !isManager
  const {
    signedAddenda,
    draft: addendumDraft,
    invalidate: refreshAddenda,
  } = useNoteAddenda(canViewAddenda ? documentReference.id : undefined)
  const [addendumEditorOpen, setAddendumEditorOpen] = useState(false)

  const handleConfirm = async () => {
    const documentReferenceId = documentReference.id
    if (!documentReferenceId || !encounterId) return

    // Option A — Lock is a clean, no-payload call. The complete note + context
    // already lives on the server: every save (debounced auto-save, explicit
    // Save, AI auto-submit) POSTs the full `draftToSoap(formState)` to
    // `/visits/:enc/soap-note`, which the backend persists VERBATIM as a
    // lossless draft blob on the preliminary DocumentReference. By the time
    // this viewer (and its Lock button) is on screen, the edit form has
    // unmounted and flushed its final save (see `<ProgressNoteEdit>`'s
    // flush-on-exit), so the stored blob == what the doctor last saw.
    //
    // The FE therefore no longer reconstructs the note from the lossy
    // QuestionnaireResponse (which dropped med dose/route/status, procedures,
    // and billing detail) and no longer force-saves on lock. /lock just
    // attests + references the doc; the backend MATERIALIZES the final
    // clinical resources from its stored blob, flips the doc to `final`, and
    // transmits.
    await new Promise<void>((resolve, reject) => {
      lock.mutate(
        {
          encounterId,
          documentReferenceId,
        },
        {
          onSuccess: () => resolve(),
          onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
        },
      )
    })
  }

  const openLockModal = () => {
    if (!documentReference.id || !encounterId) return

    modals.openConfirmModal({
      modalId: LOCK_MODAL_ID,
      // No Mantine title bar / close X — the icon + "Lock Progress Note"
      // heading lives inside <LockConfirmBody> to match the mockup.
      withCloseButton: false,
      size: 'md',
      labels: { confirm: 'Lock & Queue for TEFCA', cancel: 'Cancel' },
      confirmProps: LOCK_CONFIRM_PROPS,
      closeOnConfirm: false,
      children: <LockConfirmBody />,
      onConfirm: () => {
        // modals' onConfirm expects a void return, so run the async lock work
        // in a fire-and-forget IIFE (Mantine never awaited it anyway).
        void (async () => {
          // Flip the Confirm button into a loading + disabled state so the
          // doctor sees the mutation in flight. Cancel is also disabled
          // (mid-mutation cancel is not a supported flow — the backend has
          // no idempotent abort).
          modals.updateModal({
            modalId: LOCK_MODAL_ID,
            confirmProps: { ...LOCK_CONFIRM_PROPS, loading: true, disabled: true },
            cancelProps: { disabled: true },
            closeOnClickOutside: false,
            closeOnEscape: false,
            withCloseButton: false,
          })
          try {
            await handleConfirm()
            modals.close(LOCK_MODAL_ID)
            // Signal the parent (Documents.tsx) to re-fetch the DocumentReference
            // + Provenance so the viewer flips from preliminary → final without
            // needing a manual page reload.
            onLockSuccess?.()
          } catch {
            // `useLockAndTransmit` already surfaced a red notification via
            // `logError` + `showNotification` in its `onError`. Reset the
            // modal back to its idle state so the doctor can retry without
            // re-opening.
            modals.updateModal({
              modalId: LOCK_MODAL_ID,
              confirmProps: { ...LOCK_CONFIRM_PROPS, loading: false, disabled: false },
              cancelProps: { disabled: false },
              closeOnClickOutside: true,
              closeOnEscape: true,
              withCloseButton: false,
            })
          }
        })()
      },
    })
  }

  // PD-1149 / PD-1600 — block Lock while any row is still flagged (an implausible
  // vital, or a low-confidence AI row); the doctor must confirm/correct first.
  const hasUnresolvedVitals = useMemo(() => {
    const soapNote = readScribeDraftPayload(documentReference)?.soapNote
    if (!soapNote) return false
    // Only keys with a row the doctor can actually confirm — see
    // `resolvableLowConfidenceKeys`; counting the rest deadlocked Lock.
    return (
      (soapNote.implausibleVitals?.length ?? 0) > 0 ||
      resolvableLowConfidenceKeys(soapNote).length > 0
    )
  }, [documentReference])
  const lockDisabled = !documentReference.id || !encounterId || hasUnresolvedVitals
  const lockTooltip = !encounterId
    ? 'Encounter context missing — refresh the chart and try again.'
    : hasUnresolvedVitals
      ? 'Resolve the flagged rows (confirm or correct) before locking.'
      : undefined

  // Other statuses (entered-in-error, amended, …) — render nothing; the
  // existing <Documents> view is untouched.
  if (!isFinal && !isPreliminary) return null

  /** Action group renderers — kept inline so the mockup parity is obvious. */
  const renderActionGroup = () => {
    // External imports are read-only: no Edit, no Lock, no "Locked · TEFCA"
    // pill (we never locked or transmitted someone else's record). Only the
    // passive Review Followup / Export to PDF affordances remain.
    const showLockChrome = !isExternal
    const hasAnyAction =
      showReviewFollowup ||
      showExportToPdf ||
      (showLockChrome && (canEdit || isPreliminary || isFinal))
    if (!hasAnyAction) return null

    return (
      <Group gap="xs" justify="flex-end" wrap="wrap" data-testid="progress-note-action-group">
        {showReviewFollowup && (
          <Button
            loading={followupLoading}
            leftSection={followupSaved ? <IconCheck size={14} /> : <IconEye size={14} />}
            onClick={() => {
              // Mockup parity (ui-designs/.../ChartPage.tsx:3954) — the button
              // smooth-scrolls to the `#followup` anchor inside the rendered
              // Plan section instead of opening a modal. The caller-provided
              // `onReviewFollowup` is still invoked (if supplied) so any
              // analytics / non-UI side effects keep firing.
              document
                .getElementById('followup')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              onReviewFollowup?.()
            }}
            data-testid="progress-note-review-followup"
            // Label stays "Patient Instructions" either way; once the followup
            // has been emailed (`followupSaved`) we swap to a green check accent
            // so the doctor can see at a glance that instructions already went out.
            styles={{
              root: {
                height: 36,
                padding: '7px 14px',
                borderRadius: 7,
                border: followupSaved ? '1.5px solid #16a34a' : '1.5px solid #3b82f6',
                background: followupSaved ? '#f0fdf4' : '#eff6ff',
                color: followupSaved ? '#15803d' : '#1d4ed8',
                fontWeight: 600,
                fontSize: 12.5,
              },
            }}
          >
            Patient Instructions
          </Button>
        )}
        {showExportToPdf && (
          <Button
            loading={isExporting}
            leftSection={<IconDownload size={14} />}
            onClick={onExportToPdf}
            data-testid="progress-note-export-pdf"
            styles={{
              root: {
                height: 36,
                padding: '7px 14px',
                borderRadius: 7,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontWeight: 600,
                fontSize: 12.5,
              },
            }}
          >
            Export to PDF
          </Button>
        )}
        {/* Edit button — preliminary-only. Documents.tsx owns the edit state
            and `onToggleEdit` flips it; the viewer just renders the trigger
            so the mockup's single action group remains the source of truth.
            Coral `#e8624a` matches the mockup palette (ChartPage.tsx:3965)
            and the rest of the Progress Note edit chrome. When editing,
            button flips to a light-blue outlined state mirroring the
            mockup's "Editing…" treatment. */}
        {showLockChrome && canEdit && isPreliminary && (
          <Button
            leftSection={<IconEdit size={14} />}
            onClick={onToggleEdit}
            data-testid="progress-note-edit-toggle"
            styles={{
              root: {
                height: 36,
                padding: '7px 16px',
                borderRadius: 7,
                border: isEditing ? '1.5px solid #3b82f6' : `1.5px solid ${PROGRESS_NOTE_CORAL}`,
                background: isEditing ? '#eff6ff' : PROGRESS_NOTE_CORAL,
                color: isEditing ? '#1d4ed8' : '#fff',
                fontWeight: 700,
                fontSize: 12.5,
              },
            }}
          >
            {isEditing ? 'Editing…' : 'Edit'}
          </Button>
        )}
        {/* Lock Note — preliminary only. Mockup places this last in the
            action group (ChartPage.tsx:3972-3978). Navy filled `#1e3a5f`
            with white text + bold weight. Tooltip surfaces the
            "missing encounter" reason when the lock cannot be wired. */}
        {showLockChrome && !isManager && isPreliminary && (
          <Tooltip label={lockTooltip} disabled={!lockTooltip}>
            <Button
              leftSection={<IconLock size={14} />}
              onClick={openLockModal}
              disabled={lockDisabled}
              data-testid="progress-note-lock-button"
              styles={{
                root: {
                  height: 36,
                  padding: '7px 14px',
                  borderRadius: 7,
                  border: '1.5px solid #1e3a5f',
                  background: '#1e3a5f',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12.5,
                },
              }}
            >
              Lock Note
            </Button>
          </Tooltip>
        )}
        {/* Add Addendum — locked notes only. The original stays read-only; the
            addendum is a separate appended document. */}
        {canAmend && (
          <Button
            leftSection={<IconWriting size={14} />}
            onClick={() => setAddendumEditorOpen(true)}
            data-testid="progress-note-add-addendum"
            styles={{
              root: {
                height: 36,
                padding: '7px 14px',
                borderRadius: 7,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontWeight: 600,
                fontSize: 12.5,
              },
            }}
          >
            {addendumDraft ? 'Continue Addendum' : 'Add Addendum'}
          </Button>
        )}
        {/* Locked · TEFCA pill — structured (scribe-locked) final notes only;
            uploads + Care-Coordinator notes were never TEFCA-transmitted. */}
        {structured && isFinal && (
          <Box
            bg="#1e3a5f"
            style={{
              borderRadius: 7,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              height: 36,
            }}
            data-testid="progress-note-locked-pill"
          >
            <IconLock size={12} color="#93c5fd" />
            <Text fw={700} c="#93c5fd" style={{ fontSize: 12.5 }}>
              Locked · TEFCA
            </Text>
          </Box>
        )}
      </Group>
    )
  }

  /**
   * Locked-note banner — mockup parity with
   * `ui-designs/.../ChartPage.tsx:3919-3929`. Full-width navy ribbon with
   * lock icon, "Note Locked" headline, inline "This Progress Note is
   * finalized and read-only." subtitle, and a green
   * "TEFCA Transmission Queued" pill on the right. Only renders when
   * `docStatus === 'final'`. The exact lock timestamp lives in the Stats
   * Strip's "Locked In" card per mockup ordering — the banner keeps it out
   * of view to mirror the prototype.
   */
  // Source label for the External Record banner — prefer the Provenance
  // "source" agent display (how `Documents.tsx` surfaces document Source),
  // then the DocRef custodian, then the first author, then a generic fallback.
  const externalSource =
    provenance?.agent?.find((a) => a.type?.coding?.some((c) => c.code === 'source'))?.who
      ?.display ||
    documentReference.custodian?.display ||
    documentReference.author?.[0]?.display ||
    'External provider'

  const authoredAtFormatted = documentReference.date
    ? formatDateTime(documentReference.date, '-', true)
    : '—'
  const importedAtFormatted = documentReference.meta?.lastUpdated
    ? formatDateTime(documentReference.meta.lastUpdated, '-', true)
    : '—'

  /**
   * External Record banner — distinct read-only chrome for Health-Gorilla /
   * externally-imported Progress Notes. Slate (not navy) to visually separate
   * it from OUR "Note Locked / TEFCA" banner, with no lock / TEFCA semantics
   * (we did not author or transmit this record). Surfaces Source / authored /
   * imported provenance so the doctor knows where the record came from. The
   * CCDA tables below render the actual document content (never blank).
   */
  const externalBanner = isExternal ? (
    <Box
      bg="#475569"
      c="white"
      px="md"
      py="sm"
      style={{ borderRadius: 6 }}
      data-testid="progress-note-external-banner"
    >
      <Group justify="space-between" wrap="wrap" gap="md" align="center">
        <Group gap="sm" wrap="nowrap" align="center">
          <IconExternalLink size={16} color="#cbd5e1" />
          <Text fw={700} c="#e2e8f0" style={{ fontSize: 13 }}>
            External Record
          </Text>
          <Text c="#cbd5e1" style={{ fontSize: 12.5 }}>
            Imported from an external source · read-only
          </Text>
        </Group>
        <Group gap="lg" wrap="wrap">
          <Text c="#cbd5e1" style={{ fontSize: 11.5 }} data-testid="external-banner-source">
            Source: <strong style={{ color: '#f1f5f9' }}>{externalSource}</strong>
          </Text>
          <Text c="#cbd5e1" style={{ fontSize: 11.5 }} data-testid="external-banner-authored">
            Authored: <strong style={{ color: '#f1f5f9' }}>{authoredAtFormatted}</strong>
          </Text>
          <Text c="#cbd5e1" style={{ fontSize: 11.5 }} data-testid="external-banner-imported">
            Imported: <strong style={{ color: '#f1f5f9' }}>{importedAtFormatted}</strong>
          </Text>
        </Group>
      </Group>
    </Box>
  ) : null

  // Our "Note Locked / TEFCA" banner — final only AND not external (external
  // imports show the External Record banner above instead). Care Coordinator
  // notes have no Lock concept at all (PD-1175), so they get no banner either.
  const lockedBanner =
    isFinal && !isExternal && !isCareCoordinatorNote(documentReference) ? (
      <Box
        bg="#1e3a5f"
        c="white"
        px="md"
        py="sm"
        style={{ borderRadius: 6 }}
        data-testid="progress-note-locked-banner"
      >
        <Group justify="space-between" wrap="nowrap" gap="md">
          <Group gap="sm" wrap="nowrap" align="center">
            <IconLock size={16} color="#93c5fd" />
            <Text fw={700} c="#93c5fd" style={{ fontSize: 13 }}>
              Note Locked
            </Text>
            <Text c="#bfdbfe" style={{ fontSize: 12.5 }}>
              This Progress Note is finalized and read-only.
            </Text>
          </Group>
          {/* TEFCA pill — structured (scribe-locked) notes only; uploads + CC
            notes show the plain "Note Locked" banner without TEFCA. */}
          {structured && (
            <Box
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(34,197,94,0.4)',
              }}
              data-testid="progress-note-locked-badge"
            >
              <IconCheck size={12} color="#4ade80" strokeWidth={3} />
              <Text fw={600} c="#4ade80" style={{ fontSize: 11.5, letterSpacing: '0.01em' }}>
                TEFCA Transmission Queued
              </Text>
            </Box>
          )}
        </Group>
      </Box>
    ) : null

  return (
    <TocPresenceProvider>
      <Stack gap="md">
        {/* Banner — either our "Note Locked / TEFCA" ribbon (our final notes) or
          the slate "External Record" banner (imported notes). Mutually
          exclusive. Portaled to the slot above the title row when
          `lockedBannerTarget` is supplied; otherwise rendered inline. */}
        {(() => {
          const banner = externalBanner ?? lockedBanner
          if (!banner) return null
          return lockedBannerTarget ? createPortal(banner, lockedBannerTarget) : banner
        })()}

        {/* Action group — mockup ChartPage.tsx:3950-3987. Per the mockup, these
          buttons sit on the SAME ROW as the "Progress Note" title (rendered
          by Documents.tsx). When `actionGroupTarget` is set, we portal the
          group into that DOM node (the title row). Otherwise we render
          inline above the Stats Strip as a sensible default. */}
        {actionGroupTarget
          ? createPortal(renderActionGroup(), actionGroupTarget)
          : renderActionGroup()}

        {/* Metadata grid — mockup parity (ChartPage.tsx:3990-3997). 4-col
          read-only grid (Source / Added on / Type / Modified on / Document
          Name / – / Provider / Encounter Date) between the title row and the
          Stats Strip. Uses the same Provenance-agent extraction as the legacy
          `Documents.tsx` metadata block; Documents.tsx suppresses its own
          duplicate block when this viewer is rendering a structured note. */}
        {structured && (
          <NoteMetadataGrid
            documentReference={documentReference}
            encounter={encounter}
            provenance={provenance}
          />
        )}

        {structured && (
          <NoteStatsStrip
            encounter={encounter}
            documentReference={documentReference}
            provenance={provenance}
            // Gate-clinical-writes-until-Lock: while PRELIMINARY, the clinical
            // resources don't exist yet — count the PROPOSED items off the draft
            // QR instead of the (zero) materialized resources. Once `final` the
            // strip counts the real chart resources.
            draftCounts={isPreliminary}
            formFallback={draftFallbackCounts}
          />
        )}

        {/* Table of Contents — anchors map to the section ids mounted by
          `<LockedProgressNoteSections>` (final) or `<ProgressNoteEdit>`
          (preliminary). Always present for structured Progress Notes; the
          legacy CCDA TOC is suppressed in `Documents.tsx` so the user does
          not see two TOCs with different section lists side by side. */}
        {structured && <TableOfContents />}

        {/* Billing summary card is no longer rendered here — the mockup mounts
          a compact 2-col "Billing / Coding Summary" grid at the tail of the
          Plan section instead (`<BillingSummary>`), which reads and writes the
          visit's billing itself. */}

        {/* LD-21 — locked Progress Notes render the 5 mockup sections inline so
          the read view matches the edit view structurally. The Documents.tsx
          CCDA tables are suppressed below so the legacy "Reason for Visit" /
          "Subjective" / "Plan" CCDA panels do not duplicate the structured
          content above. Sections handle their own empty-state placeholders
          when QR / Claim data is missing (legacy notes). */}
        {structured && (
          <LockedProgressNoteSections
            documentReference={documentReference}
            provenance={provenance}
            encounter={encounter}
            encounterId={encounterId}
            patientId={patientId}
            signature={signature}
          />
        )}

        {/* Addenda — appended below the note so the original signed record
          reads first. */}
        {/* The draft is only shown to whoever can act on it — it is not part of
          the record until signed. */}
        <AddendaSection
          signedAddenda={signedAddenda}
          draft={canAmend ? addendumDraft : undefined}
        />

        {canAmend && documentReference.id && (
          <AddendumEditorModal
            opened={addendumEditorOpen}
            onClose={() => setAddendumEditorOpen(false)}
            documentReferenceId={documentReference.id}
            draft={addendumDraft}
            onChanged={() => void refreshAddenda()}
          />
        )}

        {/* Session Transcript — lazy-loads the transcript Binary via the sibling Provenance.
          Gated on the draft-payload blob, NOT the Device-agent Provenance the cascade omits. */}
        {hasScribeDraftPayload(documentReference) && provenance && !isManager && (
          <SessionTranscriptAccordion provenance={provenance} />
        )}

        {/* Footer — mockup parity (ChartPage.tsx:4416-4420). Small muted
          attribution line at the bottom of the note: practice / provider on
          the left, Added/Modified timestamps on the right. */}
        {structured && (
          <Group
            justify="space-between"
            wrap="wrap"
            gap="xs"
            mt="md"
            style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10 }}
            data-testid="progress-note-footer"
          >
            <Text style={{ fontSize: 11.5, color: '#6b7280' }}>
              {serviceOrg?.name ||
                encounter?.serviceProvider?.display ||
                'beHuman Medical Group PA'}{' '}
              · {documentReference.author?.[0]?.display || '—'}
            </Text>
            <Text style={{ fontSize: 11.5, color: '#9ca3af' }}>
              Added:{' '}
              {documentReference.date ? formatDateTime(documentReference.date, '-', true) : '—'} ·
              Modified:{' '}
              {documentReference.meta?.lastUpdated
                ? formatDateTime(documentReference.meta.lastUpdated, '-', true)
                : '—'}
            </Text>
          </Group>
        )}
      </Stack>
    </TocPresenceProvider>
  )
}

/**
 * Confirmation body for the Lock modal. Mirrors the mockup at
 * `ui-designs/src/apps/provider-portal/pages/ChartPage.tsx:4424-4452`:
 *   - 42px navy icon tile + headline + "irreversible" instruction
 *   - sky info row announcing TEFCA queue (with `IconInfoCircle`)
 */
function LockConfirmBody() {
  return (
    <Stack gap="md">
      {/* Icon + title on one row (mockup ChartPage.tsx:4424-4452). The modal
          itself renders no Mantine title bar / close button, so this is the
          heading. */}
      <Group gap="sm" align="center" wrap="nowrap">
        <Box
          className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[10px] bg-[#1e3a5f]"
          aria-hidden="true"
        >
          <IconLock size={18} color="#93c5fd" strokeWidth={2} />
        </Box>
        <Text fw={700} size="lg" c="dark.8">
          Lock Progress Note
        </Text>
      </Group>
      <Text size="sm" c="dark.7" style={{ lineHeight: 1.65 }}>
        Locking this note is <strong>irreversible</strong>. No further edits will be permitted after
        locking.
      </Text>
      <Group
        gap="xs"
        align="flex-start"
        wrap="nowrap"
        className="rounded-lg border border-[#bae6fd] bg-[#f0f9ff] px-[14px] py-[10px]"
        data-testid="lock-modal-tefca-notice"
      >
        <IconInfoCircle size={14} color="#0369a1" style={{ flexShrink: 0, marginTop: 2 }} />
        <Text size="sm" c="#0369a1" style={{ lineHeight: 1.55 }}>
          This note will be queued for <strong>TEFCA transmission</strong> to connected health
          networks.
        </Text>
      </Group>
    </Stack>
  )
}
